// Tab deep-linking + Bootstrap Popover init
//
// On every turbo:load:
//   - Activates the tab named in the ?tab= URL param (restores bookmarked tabs).
//   - Writes the active tab name back to the URL on tab switch (keeps URL in sync).
//   - Initializes Bootstrap Popovers via getOrCreateInstance (idempotent).
//
// turbo:before-cache disposes Popover instances so the snapshot contains plain
// DOM — prevents ghost Popover instances after cache restores.

import { Popover, Tab } from "bootstrap";

const tabControllers = new WeakMap();

// Locates the Bootstrap tab trigger matching a tab name from the URL.
function findTabTrigger(tabName) {
  return Array.from(
    document.querySelectorAll('#myTab a[data-bs-toggle="tab"]'),
  ).find((el) => el.getAttribute("href") === `#${tabName}`);
}

// Updates the current URL whenever a tab becomes active.
function bindTabUrlSync(el) {
  if (tabControllers.has(el)) return;

  const controller = new AbortController();
  tabControllers.set(el, controller);

  el.addEventListener(
    "shown.bs.tab",
    function () {
      const tabName = this.getAttribute("href").replace(/^#/, "");
      const url = new URL(window.location.href);
      url.searchParams.set("tab", tabName);
      history.replaceState({ path: url.toString() }, "", url.toString());
    },
    { signal: controller.signal },
  );
}

// Restores the requested tab and initializes Bootstrap popovers on the current page.
function initTabsAndPopovers() {
  const activeTab = new URLSearchParams(window.location.search).get("tab");
  let tabActivated = false;
  if (activeTab) {
    const trigger = findTabTrigger(activeTab);
    if (trigger) {
      Tab.getOrCreateInstance(trigger).show();
      tabActivated = true;
    }
  }

  // Fallback: if no ?tab= param activated a tab and the HTML hasn't pre-selected one,
  // activate the first available tab (handles pages where the first tab is conditional).
  if (
    !tabActivated &&
    !document.querySelector('#myTab [data-bs-toggle="tab"].active')
  ) {
    const firstTab = document.querySelector('#myTab a[data-bs-toggle="tab"]');
    if (firstTab) Tab.getOrCreateInstance(firstTab).show();
  }

  document.querySelectorAll('a[data-bs-toggle="tab"]').forEach(bindTabUrlSync);

  document
    .querySelectorAll("[data-bs-toggle='popover']")
    .forEach((el) => Popover.getOrCreateInstance(el));
}

document.addEventListener("turbo:load", initTabsAndPopovers);

// Clean up Bootstrap/tab state before Turbo caches the current DOM.
document.addEventListener("turbo:before-cache", () => {
  document.querySelectorAll('a[data-bs-toggle="tab"]').forEach((el) => {
    const controller = tabControllers.get(el);
    if (!controller) return;
    controller.abort();
    tabControllers.delete(el);
  });

  // Reset tab DOM state so the cached snapshot doesn't preserve stale active-tab
  // content (e.g. spinner-replaced links or outdated charge lists). turbo:load
  // re-activates the right tab from the ?tab= URL param on restore.
  document.querySelectorAll("#myTab .nav-link").forEach((el) => {
    el.classList.remove("active");
  });
  document.querySelectorAll(".tab-pane").forEach((el) => {
    el.classList.remove("show", "active");
  });

  document.querySelectorAll("[data-bs-toggle='popover']").forEach((el) => {
    Popover.getInstance(el)?.dispose();
  });
});
